import defaults from '../config/signInBranding.json';

export const POWERED_BY = defaults.poweredBy;
export const BRANDS = defaults.brands;
export const HOSTNAMES = defaults.hostnames;

// '1.12.094-beta' -> '1.12.094 – Beta' (en dash, capital B). Derived from the version string, never hard-coded.
export function webVersionLabel(version) {
    const [number, ...stage] = String(version || '').split('-');
    const label = stage.join('-');
    return label ? `${number} – ${label.charAt(0).toUpperCase()}${label.slice(1)}` : number;
}

// Footer on every sign-in screen: "Powered by smarTEKnowledgy · v<version>". The caller supplies the version.
export function poweredByLine(version) {
    return `${defaults.poweredBy} · v${version}`;
}

// Browser tab title: "<Page> · <Brand>", brand spelled exactly as configured.
export function pageTitle(page, brandName) {
    return `${page} · ${brandName}`;
}
export const FALLBACK_BRANDING = { logo_url: null, brand_name: defaults.fallbackBrandName };
export const BRANDING_PATH = '/api/public/branding';

export function validBranding(value) {
    return value && typeof value.brand_name === 'string' && value.brand_name.trim()
        && (value.logo_url === null || (typeof value.logo_url === 'string' && /^https:\/\/[^\s]+$/i.test(value.logo_url)));
}

export function readCachedBranding(key, fallback = FALLBACK_BRANDING) {
    try {
        const cached = JSON.parse(window.localStorage.getItem(key));
        return validBranding(cached) ? cached : fallback;
    } catch (error) {
        console.warn('Sign-in branding cache unavailable:', error.message);
        return fallback;
    }
}

export async function loadBranding(url, signal) {
    const response = await fetch(url, { credentials: 'omit', signal });
    if (!response.ok) throw new Error('Branding could not be loaded.');
    const value = await response.json();
    if (!validBranding(value)) throw new Error('Branding response is invalid.');
    return { logo_url: value.logo_url, brand_name: value.brand_name.trim() };
}

// ---- BL-407: the address selects the sign-in door; ?brand remains only for legacy-link handling. ----
export const NEUTRAL_BRANDING = { brand_name: 'IntelAgent', logo_url: null, accent: null, slug: null, neutral: true };
export const DEVICE_BRAND_KEY = 'intelAgentSignInBrand';
const WELCOME_PENDING_KEY = 'intelAgentWelcomePending';
const WELCOME_PENDING_TTL_MS = 10 * 60 * 1000;
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;
const ACCENT_PATTERN = /^#[0-9a-f]{6}$/i;
const SEQUENCES = ['film_finale', 'fade', 'off'];

export function validSlug(value) {
    return typeof value === 'string' && SLUG_PATTERN.test(value);
}

export function brandSlugFromUrl(href = window.location.href) {
    try {
        const slug = new URL(href).searchParams.get('brand');
        return validSlug(slug) ? slug : null;
    } catch (_) {
        return null;
    }
}

// Only public display fields survive: name, https logo, hex accent, slug, and the welcome setting.
export function cleanDeviceBrand(value) {
    if (!validBranding(value)) return null;
    return {
        brand_name: value.brand_name.trim(),
        logo_url: value.logo_url || null,
        accent: ACCENT_PATTERN.test(value.accent || '') ? value.accent : null,
        slug: validSlug(value.slug) ? value.slug : null,
        signin_sequence: SEQUENCES.includes(value.signin_sequence) ? value.signin_sequence : null,
    };
}

export function readDeviceBrand(storage = window.localStorage) {
    try {
        return cleanDeviceBrand(JSON.parse(storage.getItem(DEVICE_BRAND_KEY)));
    } catch (_) {
        return null;
    }
}

export function writeDeviceBrand(value, storage = window.localStorage) {
    const clean = cleanDeviceBrand(value);
    if (!clean) return null;
    try { storage.setItem(DEVICE_BRAND_KEY, JSON.stringify(clean)); } catch (_) { /* private mode: brand just isn't remembered */ }
    return clean;
}

export async function loadBrandBySlug(slug, signal) {
    const response = await fetch(`${BRANDING_PATH}?slug=${encodeURIComponent(slug)}`, { credentials: 'omit', signal });
    if (!response.ok) throw new Error('Branding could not be loaded.');
    const value = await response.json();
    const clean = cleanDeviceBrand({ ...value, slug });
    if (!clean) throw new Error('Branding response is invalid.');
    return clean;
}

// SSO leaves the page, so the welcome is armed before the redirect and picked up when we land back.
export function markWelcomePending(storage = window.sessionStorage) {
    try { storage.setItem(WELCOME_PENDING_KEY, String(Date.now())); } catch (_) { /* no welcome, no harm */ }
}

export function takeWelcomePending(href = window.location.href, storage = window.sessionStorage) {
    let stamp = null;
    try {
        stamp = Number(storage.getItem(WELCOME_PENDING_KEY));
        storage.removeItem(WELCOME_PENDING_KEY);
    } catch (_) {
        return false;
    }
    if (!stamp || Date.now() - stamp > WELCOME_PENDING_TTL_MS) return false;
    // Only an OAuth landing counts; a plain reload with a stale flag does not.
    try {
        const url = new URL(href);
        return url.searchParams.has('code') || url.searchParams.has('error') || /access_token=|error=/.test(url.hash);
    } catch (_) {
        return false;
    }
}

// BL-402.1: brands whose sign-in shows the Atlanta skyline photo behind a see-through card.
export const PHOTO_BACKDROP_SLUG = 'the-ride-atl';

// Only the brand actually on screen decides the look; the neutral IntelAgent look never gets the photo.
export function usesPhotoBackdrop(brand) {
    return brand?.slug === PHOTO_BACKDROP_SLUG;
}

// Start fetching the photo as soon as the sign-in screen knows it may be needed (link slug or cached brand).
export function photoBackdropLikely(brand, href = window.location.href) {
    return usesPhotoBackdrop(brand) || brandSlugFromUrl(href) === PHOTO_BACKDROP_SLUG;
}
